import { PlaybackEngine, type PlaybackItem, type PlaybackPlan } from './playback.js';

/**
 * Renderer entry point.
 *
 * Shows one of three states: the pairing screen, playback, or a "nothing scheduled" holding
 * screen. It never fetches anything itself — the main process supplies the plan and the media
 * URLs, so playback works identically online and offline.
 */

interface PairingState {
  status: 'idle' | 'requesting' | 'waiting' | 'paired' | 'error';
  code: string | null;
  expiresAt: string | null;
  apiBaseUrl: string;
  message: string | null;
  screenName?: string | null;
  organizationName?: string | null;
}

interface RuntimeStatus {
  online: boolean;
  paired: boolean;
  syncState: string;
  activeContentVersion: string | null;
  lastHeartbeatAt: string | null;
  lastSyncAt: string | null;
  /** Last successful contact with the server; reported by the runtime. */
  lastContactAt?: string | null;
  /** Out of contact too long: show the offline notice (only hosts that enable it set this). */
  offlineNotice?: boolean;
  lastError: string | null;
  cachedBytes: number;
  pendingProofOfPlay: number;
}

declare global {
  interface Window {
    signage: {
      getState(): Promise<{
        plan: PlaybackPlan | null;
        status: RuntimeStatus | null;
        pairing: PairingState | null;
        /** Where cached media is served from; the file's hash is appended to it. */
        mediaUrlPrefix?: string;
      }>;
      mediaUrl(sha256: string): Promise<string>;
      requestSync(): Promise<RuntimeStatus | null>;
      refreshPlan(): Promise<PlaybackPlan | null>;
      reportNowPlaying(info: {
        playlistId: string | null;
        mediaId: string | null;
        scheduleId: string | null;
      }): void;
      reportProofOfPlay(entry: unknown): void;
      reportEvent(entry: unknown): void;
      heartbeat(): void;
      onPlan(handler: (plan: PlaybackPlan) => void): () => void;
      onStatus(handler: (status: RuntimeStatus) => void): () => void;
      onPairing(handler: (state: PairingState) => void): () => void;
      onIdentify(handler: (payload: { durationMs: number }) => void): () => void;
      /** Choosing the device's home app. Android only; the others have none or answer null. */
      homeScreen?: {
        status(): Promise<{ isHome: boolean; canChoose: boolean; overlayAllowed: boolean } | null>;
        choose(): Promise<{ opened: boolean }>;
      };
    };
  }
}

const stage = document.getElementById('stage') as HTMLElement;
const pairingScreen = document.getElementById('pairing') as HTMLElement;
const idleScreen = document.getElementById('idle') as HTMLElement;
const overlay = document.getElementById('overlay') as HTMLElement;

let engine: PlaybackEngine | null = null;
let lastStatus: RuntimeStatus | null = null;
let overlayTimer: ReturnType<typeof setTimeout> | null = null;
/** True while the pairing panel is on screen. */
let pairingVisible = false;
/**
 * Where the host serves cached media from, with the file's hash appended.
 *
 * Supplied by the host through `getState`, because only it knows how its cache is reachable:
 * Electron registers a `media://` protocol, the Android player goes through Capacitor's file
 * bridge. Defaulted to Electron's scheme so the surface still works if a host omits it.
 */
let mediaUrlPrefix = 'media://';

function show(element: HTMLElement, visible: boolean): void {
  element.classList.toggle('hidden', !visible);
  if (element === pairingScreen || element === idleScreen) void updateHomeSetup();
}

/* ----------------------------------------------------------------- home screen */

/**
 * On Android, the pairing and idle screens offer to make the player the device's home app, so the
 * screen opens it by itself every time the TV is turned on. Never shown over content, and gone
 * once the player is the home app. Where the device has no picker (Google TV), it says the
 * installer sets it once from a computer.
 */
const homeSetup = document.getElementById('home-setup') as HTMLElement;
const homeButton = document.getElementById('home-button') as HTMLButtonElement;
const homeNote = document.getElementById('home-note') as HTMLElement;

async function updateHomeSetup(): Promise<void> {
  const onSetupScreen = !pairingScreen.classList.contains('hidden') || !idleScreen.classList.contains('hidden');
  const status = onSetupScreen ? await window.signage.homeScreen?.status().catch(() => null) : null;
  const offer = Boolean(status && !status.isHome);
  homeSetup.classList.toggle('hidden', !offer);
  document.body.classList.toggle('home-offer', offer);
  if (!status || !offer) return;
  homeButton.classList.toggle('hidden', !status.canChoose);
  homeNote.textContent = status.canChoose
    ? 'Then this screen opens Oicas Signage by itself every time the TV is turned on.'
    : 'On this TV, the home screen is set once from a computer with the Oicas TV setup tool.' +
      (status.overlayAllowed
        ? ''
        : ' Until then, allow "Display over other apps" for Oicas Signage Player in Settings → Apps, so it starts after a power cut.');
  // A TV remote has no pointer: put the button under the select key.
  if (status.canChoose && document.activeElement !== homeButton) homeButton.focus();
}

homeButton.addEventListener('click', () => {
  void window.signage.homeScreen?.choose().then(({ opened }) => {
    if (!opened) void updateHomeSetup();
  });
});
// Coming back from Android's picker: hide the offer if the player is now the home app.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') void updateHomeSetup();
});

/* -------------------------------------------------------------------- playback */

function ensureEngine(): PlaybackEngine {
  if (engine) return engine;
  engine = new PlaybackEngine(
    stage,
    {
      onItemStart: (item: PlaybackItem) => {
        // Something is on screen again: drop the logo screen that a failed pass may have shown.
        show(idleScreen, false);
        window.signage.reportNowPlaying({
          playlistId: item.playlistId,
          // A menu page is not a file; the schedule and playlist still say what is showing.
          mediaId: item.kind === 'menu' ? null : item.mediaId,
          scheduleId: item.scheduleId,
        });
      },
      onItemComplete: (item, startedAt, completedAt, completed) => {
        window.signage.reportProofOfPlay({
          mediaId: item.kind === 'menu' ? null : item.mediaId,
          playlistId: item.playlistId,
          slideshowId: item.slideshowId,
          scheduleId: item.scheduleId,
          startedAt: startedAt.toISOString(),
          completedAt: completedAt.toISOString(),
          durationMs: Math.max(0, completedAt.getTime() - startedAt.getTime()),
          completed,
        });
      },
      onError: (code, message, context) => {
        // Console output is captured into the player log by the main process.
        console.error(`playback ${code}: ${message}`, JSON.stringify(context ?? {}));
        window.signage.reportEvent({ level: 'error', code, message, context });
      },
      onIdle: () => {
        show(idleScreen, true);
      },
    },
    (sha256) => `${mediaUrlPrefix}${sha256}`,
  );
  return engine;
}

/** The latest plan, so the idle screen can be shown again when pairing finishes. */
let lastPlan: PlaybackPlan | null = null;

/** Shows the logo screen when there is nothing to play, with a hint for whoever sets it up. */
function showIdleIfEmpty(plan: PlaybackPlan | null): void {
  const hasContent = Boolean(plan && plan.items.length > 0);
  show(idleScreen, !hasContent);
  const idleReason = document.getElementById('idle-reason');
  if (idleReason && !hasContent) {
    idleReason.textContent =
      plan && plan.unavailableMediaIds.length > 0
        ? 'Downloading content…'
        : 'Add content to this screen in your dashboard.';
  }
}

function applyPlan(plan: PlaybackPlan | null): void {
  if (!plan) return;
  lastPlan = plan;
  show(pairingScreen, false);
  showIdleIfEmpty(plan);
  ensureEngine().setPlan(plan);
}

/* --------------------------------------------------------------------- pairing */

function applyPairing(state: PairingState | null): void {
  if (!state) return;
  // `idle` means the pairing flow has not started, which is the normal state of a device that
  // was already paired when it booted: the host still reports it whenever the page loads.
  // Treating it as "needs pairing" covered a playing screen with the pairing panel after
  // every restart. It says nothing either way, so the panel is left as it is: the plan hides
  // it on a paired device, and an unpaired one moves on to `requesting` straight away.
  if (state.status === 'idle') return;
  if (state.status === 'paired') {
    show(pairingScreen, false);
    pairingVisible = false;
    updateOfflineNotice();
    // A newly paired screen usually has nothing assigned yet, and its (empty) plan was already
    // delivered before pairing began, so no new plan arrives to show the logo screen: show it
    // here. Content assigned later replaces it as soon as its plan arrives.
    showIdleIfEmpty(lastPlan);
    updateConnectionDot();
    return;
  }

  show(pairingScreen, true);
  show(idleScreen, false);
  pairingVisible = true;
  updateConnectionDot();
  updateOfflineNotice();

  const codeEl = document.getElementById('pairing-code') as HTMLElement;
  const messageEl = document.getElementById('pairing-message') as HTMLElement;
  const serverEl = document.getElementById('pairing-server') as HTMLElement;

  if (state.code) {
    codeEl.textContent = state.code;
    codeEl.classList.remove('placeholder');
  } else {
    codeEl.textContent = state.status === 'error' ? '— — — — — —' : 'Loading…';
    codeEl.classList.add('placeholder');
  }
  messageEl.textContent =
    state.message ??
    (state.status === 'waiting'
      ? 'Enter this code in your dashboard under Screens → Add screen.'
      : 'Contacting the server…');
  serverEl.textContent = state.apiBaseUrl;
}

/* ---------------------------------------------------------------------- status */

/**
 * Paints the connection indicator.
 *
 * Suppressed entirely during pairing: an unpaired device sends no heartbeats, so the runtime
 * reports itself offline even while it is plainly reaching the server to fetch a code. Showing a
 * red dot there would contradict the panel underneath it, which reports reachability on its own.
 */
function updateConnectionDot(): void {
  const dot = document.getElementById('status-dot');
  if (!dot) return;

  if (pairingVisible) {
    dot.className = 'status-dot pairing';
    dot.title = '';
    return;
  }

  const online = lastStatus?.online ?? false;
  dot.className = `status-dot ${online ? 'online' : 'offline'}`;
  dot.title = online ? 'Connected' : `Offline: ${lastStatus?.lastError ?? 'no connection'}`;
}

/**
 * The long-offline notice: a bar along the bottom, over content that keeps playing, in both
 * languages (a screen has no viewer to pick one). The runtime decides when; it goes by itself as
 * soon as the server answers again.
 */
const offlineNotice = document.getElementById('offline-notice') as HTMLElement;

function updateOfflineNotice(): void {
  const visible = Boolean(lastStatus?.offlineNotice) && !pairingVisible;
  offlineNotice.classList.toggle('hidden', !visible);
  document.body.classList.toggle('offline-notice', visible);
}

function applyStatus(status: RuntimeStatus): void {
  lastStatus = status;
  updateConnectionDot();
  updateOfflineNotice();
  if (status.syncState === 'syncing') {
    showOverlay('Downloading new content…');
  } else if (overlay.classList.contains('visible') && status.syncState !== 'syncing') {
    hideOverlay();
  }
}

function showOverlay(text: string): void {
  const label = document.getElementById('overlay-text');
  if (label) label.textContent = text;
  overlay.classList.add('visible');
  if (overlayTimer) clearTimeout(overlayTimer);
  // Never let an overlay obscure content indefinitely.
  overlayTimer = setTimeout(() => hideOverlay(), 60_000);
}

function hideOverlay(): void {
  overlay.classList.remove('visible');
  if (overlayTimer) clearTimeout(overlayTimer);
  overlayTimer = null;
}

/* ------------------------------------------------------------------ diagnostics */

function toggleDiagnostics(): void {
  const panel = document.getElementById('diagnostics');
  if (!panel) return;
  const visible = panel.classList.toggle('visible');
  if (!visible || !lastStatus) return;
  panel.innerHTML = `
    <h2>Player diagnostics</h2>
    <dl>
      <dt>Connection</dt><dd>${lastStatus.online ? 'online' : 'offline'}</dd>
      <dt>Paired</dt><dd>${lastStatus.paired ? 'yes' : 'no'}</dd>
      <dt>Sync state</dt><dd>${lastStatus.syncState}</dd>
      <dt>Content version</dt><dd>${lastStatus.activeContentVersion ?? '—'}</dd>
      <dt>Last heartbeat</dt><dd>${lastStatus.lastHeartbeatAt ?? 'never'}</dd>
      <dt>Last sync</dt><dd>${lastStatus.lastSyncAt ?? 'never'}</dd>
      <dt>Last contact</dt><dd>${lastStatus.lastContactAt ?? '—'}</dd>
      <dt>Cached</dt><dd>${(lastStatus.cachedBytes / 1024 / 1024).toFixed(1)} MB</dd>
      <dt>Queued plays</dt><dd>${lastStatus.pendingProofOfPlay}</dd>
      <dt>Last error</dt><dd>${lastStatus.lastError ?? 'none'}</dd>
    </dl>
    <p class="hint">Press D to close · S to force a sync</p>
  `;
}

/* ------------------------------------------------------------------ bootstrap */

async function main(): Promise<void> {
  window.signage.onPlan((plan) => applyPlan(plan));
  window.signage.onStatus((status) => applyStatus(status));
  window.signage.onPairing((state) => applyPairing(state));
  window.signage.onIdentify(({ durationMs }) => ensureEngine().flashIdentify(durationMs));

  const state = await window.signage.getState();
  // Read before anything is rendered: the engine resolves every image and video through it.
  if (state.mediaUrlPrefix) mediaUrlPrefix = state.mediaUrlPrefix;
  if (state.pairing) applyPairing(state.pairing);
  if (state.status) applyStatus(state.status);
  if (state.plan) applyPlan(state.plan);

  // The main-process watchdog reloads the page if these stop arriving.
  setInterval(() => window.signage.heartbeat(), 5_000);
  window.signage.heartbeat();

  document.addEventListener('keydown', (event) => {
    if (event.key === 'd' || event.key === 'D') toggleDiagnostics();
    if (event.key === 's' || event.key === 'S') void window.signage.requestSync();
  });

  window.addEventListener('error', (event) => {
    window.signage.reportEvent({
      level: 'error',
      code: 'renderer.error',
      message: event.message,
      context: { filename: event.filename, line: event.lineno },
    });
  });
  window.addEventListener('unhandledrejection', (event) => {
    window.signage.reportEvent({
      level: 'error',
      code: 'renderer.unhandled_rejection',
      message: String(event.reason),
    });
  });
}

/**
 * Starts the playback surface.
 *
 * Deliberately not self-starting. The surface subscribes to `window.signage` the moment it runs,
 * so it must not begin until its host has installed that. Electron gets the guarantee from a
 * preload script; the Android player installs the bridge and then calls this. Relying on script
 * order in the page instead was a real bug: the surface raced the bootstrap and came up blank.
 */
export function startPlaybackSurface(): void {
  void main();
}
